/**
 * Account detail + visit-notes timeline.
 *
 *   ┌──────────────────────────────────────┐
 *   │  ← Back to Today                     │
 *   ├──────────────────────────────────────┤
 *   │  Account header (name, address,      │
 *   │  outcome, pin button, log button)    │
 *   ├──────────────────────────────────────┤
 *   │  Notes timeline (newest first)       │
 *   │   • visit_date — rep — outcome chip  │
 *   │     full note text                   │
 *   │   • …                                │
 *   └──────────────────────────────────────┘
 */

import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditNoteIcon from '@mui/icons-material/EditNote';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  type FieldAccountSummary,
  type VisitNote,
  VISIT_CHANNEL_LABELS,
  VISIT_OUTCOME_LABELS,
  VISIT_OUTCOME_TONES,
  fetchAccountDetail,
  fetchAccountTimeline,
  pinAccount,
  unpinAccount,
} from '../../api/field';
import { isRepSurfacePath, useActAsRep } from '../../api/actAs';
import { SiteLayout } from '../../components/SiteLayout';
import { AccountContactsPanel } from './AccountContactsPanel';
import { toSixPacks } from '../../lib/volume';
import { EditedMarker } from './EditedMarker';
import { VisitNoteDialog } from './VisitNoteDialog';
import { parseBackendDate } from '../../util/dates';

const INK = '#0f0a07';
const GOLD = '#bb8c3f';
const GOLD_LIGHT = '#e9c46a';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

export function FieldAccountDetailPage() {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  // Path-gated like every other consumer. This route always satisfies
  // the predicate today, so raw and gated agree — but "agrees by
  // accident" is how the earlier leaks started, and a future route
  // change shouldn't be able to make this the one place that differs.
  const { pathname } = useLocation();
  const storedActAs = useActAsRep();
  const actingAs = isRepSurfacePath(pathname) ? storedActAs : null;
  const queryClient = useQueryClient();
  const [logOpen, setLogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<VisitNote | null>(null);

  const id = accountId ? Number(accountId) : NaN;

  const detailQuery = useQuery({
    queryKey: ['field', 'account', id],
    queryFn: () => fetchAccountDetail(id),
    enabled: Number.isFinite(id),
  });

  const pinMutation = useMutation({
    mutationFn: (next: boolean) => (next ? pinAccount(id) : unpinAccount(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field', 'account', id] });
      queryClient.invalidateQueries({ queryKey: ['field', 'today'] });
      queryClient.invalidateQueries({ queryKey: ['field', 'accounts'] });
    },
  });

  if (!Number.isFinite(id)) {
    return (
      <SiteLayout subtitle="Regional Sales">
        <Container maxWidth="md" sx={{ pt: 5 }}>
          <Alert severity="error" sx={{ fontFamily: FONT_INTER }}>
            Invalid account id.
          </Alert>
        </Container>
      </SiteLayout>
    );
  }

  const data = detailQuery.data;

  return (
    <SiteLayout subtitle="Regional Sales">
      <Container maxWidth="md" sx={{ pt: { xs: 3, md: 5 }, pb: { xs: 4, md: 6 } }}>
        <Box
          component={Link}
          // While acting as a rep, the workspace is theirs at the
          // view-as route — "/regional-sales" is the ADMIN's own, so a
          // plain back link dropped an impersonating admin into their
          // own empty workspace. Only used when there is no history to
          // pop, but that is exactly the reloaded-deep-link case.
          to={actingAs ? `/admin/regional-sales/reps/${actingAs.id}/view` : '/regional-sales'}
          onClick={(e) => {
            if (window.history.length > 1) {
              e.preventDefault();
              navigate(-1);
            }
          }}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            mb: 2,
            color: GOLD,
            textDecoration: 'none',
            fontFamily: FONT_INTER,
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 16 }} />
          Back
        </Box>

        {detailQuery.isLoading && (
          <Stack spacing={2}>
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1.5 }} />
            <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1.25 }} />
            <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1.25 }} />
          </Stack>
        )}

        {detailQuery.error && (
          <Alert severity="error" sx={{ fontFamily: FONT_INTER }}>
            {(detailQuery.error as Error).message}
          </Alert>
        )}

        {data && (
          <>
            {/* Account header */}
            <Box
              sx={{
                p: { xs: 2, md: 2.5 },
                bgcolor: 'rgba(253, 251, 245, 0.85)',
                border: '1px solid rgba(187, 140, 63, 0.22)',
                borderRadius: 1.5,
                mb: 3,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    component="h1"
                    sx={{
                      fontFamily: FONT_INTER,
                      fontWeight: 700,
                      fontSize: { xs: 20, md: 24 },
                      color: INK,
                      letterSpacing: '-0.01em',
                      lineHeight: 1.2,
                    }}
                  >
                    {data.summary.name}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.5,
                      fontFamily: FONT_INTER,
                      fontSize: 13.5,
                      color: 'rgba(15, 10, 7, 0.65)',
                    }}
                  >
                    {[
                      data.summary.address,
                      data.summary.city,
                      data.summary.state_code,
                      data.summary.zip_code,
                    ]
                      .filter(Boolean)
                      .join(', ') || '— no address on file'}
                  </Typography>
                  {data.summary.premises_type && (
                    <Chip
                      size="small"
                      label={
                        data.summary.premises_type === 'ON'
                          ? 'On-premise'
                          : data.summary.premises_type === 'OFF'
                            ? 'Off-premise'
                            : data.summary.premises_type
                      }
                      sx={{
                        mt: 1,
                        height: 22,
                        fontSize: 11,
                        fontFamily: FONT_INTER,
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        bgcolor: 'rgba(50, 110, 184, 0.10)',
                        color: '#326eb8',
                      }}
                    />
                  )}
                  {/* Rep-added and not yet in a depletions file. Worth
                      badging: it explains why the account has no
                      shipment history, and tells the rep the
                      distributor data hasn't caught up. Disappears on
                      its own once the ingest confirms it. */}
                  {data.summary.source === 'rep' && (
                    <Tooltip title="Manually added — no depletions file has confirmed this account yet.">
                      <Chip
                        size="small"
                        icon={<AddBusinessOutlinedIcon sx={{ fontSize: 13 }} />}
                        label="Manually added"
                        sx={{
                          mt: 1,
                          ml: data.summary.premises_type ? 0.75 : 0,
                          height: 22,
                          fontSize: 11,
                          fontFamily: FONT_INTER,
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          bgcolor: 'rgba(187, 140, 63, 0.14)',
                          color: '#8a6526',
                          border: '1px solid rgba(187, 140, 63, 0.3)',
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>

                <Stack direction="row" spacing={1} alignItems="center">
                  <IconButton
                    aria-label={data.summary.is_pinned ? 'Unpin account' : 'Pin account'}
                    onClick={() => pinMutation.mutate(!data.summary.is_pinned)}
                    disabled={pinMutation.isPending}
                    sx={{ color: data.summary.is_pinned ? GOLD : 'rgba(15, 10, 7, 0.45)' }}
                  >
                    {data.summary.is_pinned ? <PushPinIcon /> : <PushPinOutlinedIcon />}
                  </IconButton>
                  <Button
                    variant="contained"
                    startIcon={<EditNoteIcon />}
                    onClick={() => setLogOpen(true)}
                    sx={{
                      background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`,
                      color: INK,
                      fontFamily: FONT_INTER,
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      fontSize: 12,
                      px: 2,
                      boxShadow: 'none',
                      '&:hover': {
                        background: `linear-gradient(135deg, #efc960 0%, #c79747 55%, #936e2b 100%)`,
                        boxShadow: '0 4px 14px rgba(187, 140, 63, 0.22)',
                      },
                    }}
                  >
                    Log Visit
                  </Button>
                </Stack>
              </Box>

              {/* Last activity summary line */}
              {data.summary.last_visit_date && (
                <Box
                  sx={{
                    mt: 2,
                    pt: 2,
                    borderTop: '1px dashed rgba(187, 140, 63, 0.22)',
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap',
                    fontFamily: FONT_INTER,
                    fontSize: 12.5,
                    color: 'rgba(15, 10, 7, 0.65)',
                  }}
                >
                  <span>
                    Last touched <strong>{data.summary.days_since_last_visit}</strong> day
                    {data.summary.days_since_last_visit === 1 ? '' : 's'} ago
                  </span>
                  {data.summary.next_eligible_date && (
                    <span>
                      Next eligible <strong>{data.summary.next_eligible_date}</strong>
                    </span>
                  )}
                </Box>
              )}
            </Box>

            {/* Depletions performance — last shipment, 12-mo cases,
                product count, customer-since, activity bucket. */}
            <PerformancePanel summary={data.summary} />

            {/* Who we deal with here. Its own panel rather than a line
                in the header: an account can have several contacts,
                and they're editable from here — which is the surface
                that stops contact details being write-once. */}
            <AccountContactsPanel accountId={id} contacts={data.contacts} />

            {/* Notes timeline */}
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: GOLD,
                mb: 1.5,
              }}
            >
              Activity Timeline
            </Typography>
            {data.notes.length === 0 ? (
              <Box
                sx={{
                  p: 3,
                  textAlign: 'center',
                  border: '1px dashed rgba(187, 140, 63, 0.32)',
                  borderRadius: 1.5,
                  bgcolor: 'rgba(187, 140, 63, 0.04)',
                  fontFamily: FONT_INTER,
                  color: 'rgba(15, 10, 7, 0.6)',
                  fontSize: 13.5,
                }}
              >
                No visit notes yet. Log your first one above.
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {data.notes.map((n) => (
                  <NoteRow key={n.id} note={n} onEdit={() => setEditingNote(n)} />
                ))}
              </Stack>
            )}

            <AccountProvenance accountId={id} />
          </>
        )}
      </Container>

      {data && (
        <VisitNoteDialog
          open={logOpen}
          accountId={id}
          accountName={data.summary.name}
          onClose={() => setLogOpen(false)}
        />
      )}
      {data && editingNote && (
        <VisitNoteDialog
          open
          accountId={id}
          accountName={data.summary.name}
          note={editingNote}
          onClose={() => setEditingNote(null)}
        />
      )}
    </SiteLayout>
  );
}

/**
 * Where this account came from.
 *
 * Answers the question a rep asks when an unfamiliar account shows up
 * in their list — who added it, and has the depletions feed confirmed
 * it yet. Rendered under the visit notes because it's reference
 * material, not the day's work.
 *
 * Hidden entirely when there's nothing to say: a feed account created
 * before the timeline existed has no events, and an empty section
 * reads as broken rather than as "nothing to report".
 */
function AccountProvenance({ accountId }: { accountId: number }) {
  const query = useQuery({
    queryKey: ['field', 'account', accountId, 'timeline'],
    queryFn: () => fetchAccountTimeline(accountId),
    // Provenance changes at most once a week (when a file lands), so
    // there's no reason to re-fetch this on every focus.
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  const events = query.data?.events ?? [];
  if (query.isPending || events.length === 0) return null;

  return (
    <Box sx={{ mt: 4 }}>
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: GOLD,
          mb: 1.5,
        }}
      >
        Account History
      </Typography>

      <Stack spacing={0}>
        {events.map((e, i) => (
          <Stack key={e.id} direction="row" spacing={1.5} alignItems="flex-start">
            {/* Rail: dot per event, connector between them. The last
                event has no connector, so the rail stops rather than
                trailing into nothing. */}
            <Stack alignItems="center" sx={{ pt: 0.6, flexShrink: 0 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: e.event_type === 'possible_duplicate' ? '#b22f2f' : GOLD,
                  flexShrink: 0,
                }}
              />
              {i < events.length - 1 && (
                <Box
                  sx={{
                    width: '1px',
                    flexGrow: 1,
                    minHeight: 26,
                    bgcolor: 'rgba(187, 140, 63, 0.3)',
                  }}
                />
              )}
            </Stack>

            <Box sx={{ pb: i < events.length - 1 ? 2 : 0, minWidth: 0 }}>
              <Typography
                sx={{ fontFamily: FONT_INTER, fontSize: 13, fontWeight: 600, color: INK }}
              >
                {e.summary}
              </Typography>
              <Typography
                sx={{ fontFamily: FONT_INTER, fontSize: 11.5, color: 'rgba(15, 10, 7, 0.5)' }}
              >
                {new Date(e.occurred_at).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

function NoteRow({ note, onEdit }: { note: VisitNote; onEdit: () => void }) {
  const tone = VISIT_OUTCOME_TONES[note.outcome];
  return (
    <Box
      sx={{
        bgcolor: 'rgba(253, 251, 245, 0.78)',
        border: '1px solid rgba(187, 140, 63, 0.18)',
        borderRadius: 1.25,
        p: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          flexWrap: 'wrap',
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontWeight: 700,
            fontSize: 13.5,
            color: INK,
          }}
        >
          {note.visit_date}
        </Typography>
        <Chip
          label={VISIT_OUTCOME_LABELS[note.outcome]}
          size="small"
          sx={{
            height: 20,
            fontSize: 10.5,
            fontFamily: FONT_INTER,
            fontWeight: 700,
            letterSpacing: '0.06em',
            bgcolor: `${tone}1a`,
            color: tone,
            border: `1px solid ${tone}55`,
          }}
        />
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 11.5,
            color: 'rgba(15, 10, 7, 0.55)',
            ml: 'auto',
          }}
        >
          {VISIT_CHANNEL_LABELS[note.channel]} · {note.rep_name}
          <EditedMarker
            editedAt={note.edited_at}
            editedByName={note.edited_by_name}
            someoneElse={note.edited_by_someone_else}
          />
        </Typography>
        {note.can_edit && (
          <Tooltip title="Edit this visit">
            <IconButton
              size="small"
              onClick={onEdit}
              aria-label="Edit this visit"
              sx={{ p: 0.5, color: 'rgba(15, 10, 7, 0.45)', '&:hover': { color: GOLD } }}
            >
              <EditOutlinedIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 13.5,
          color: 'rgba(15, 10, 7, 0.78)',
          lineHeight: 1.55,
          whiteSpace: 'pre-wrap',
        }}
      >
        {note.note_text}
      </Typography>

      {/* Rep-reported product lines. Labelled as such deliberately —
          these are what the rep said moved, NOT distributor shipment
          data, and the two must never be read as the same number. */}
      {note.products.length > 0 && (
        <Box sx={{ mt: 1.25 }}>
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'rgba(15, 10, 7, 0.42)',
              mb: 0.6,
            }}
          >
            Reported by rep
          </Typography>
          <Stack direction="row" gap={0.6} flexWrap="wrap" rowGap={0.6}>
            {note.products.map((p) => (
              <Chip
                key={p.product_id}
                size="small"
                label={
                  p.quantity
                    ? `${formatCases(p.quantity)} ${p.unit} · ${p.product_name}`
                    : p.product_name
                }
                sx={{
                  height: 21,
                  fontFamily: FONT_INTER,
                  fontSize: 11,
                  fontWeight: 600,
                  bgcolor: 'rgba(187, 140, 63, 0.12)',
                  color: '#8a6526',
                  border: '1px solid rgba(187, 140, 63, 0.28)',
                }}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

const ACTIVITY_BUCKET_TONE: Record<string, string> = {
  Active: '#22865a',
  '1-2 mo quiet': '#a86b1e',
  '2-3 mo quiet': '#b25a1e',
  '3-4 mo quiet': '#a13a3a',
  'Dormant 4+ mo': '#7d3c4a',
};

function formatMonthYear(iso: string | null): string | null {
  if (!iso) return null;
  // First-of-month date-only value — UTC parsing would show the prior month.
  const d = parseBackendDate(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, { month: 'short', year: 'numeric' });
}

function formatCases(value: string | null): string {
  if (!value) return '0';
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return n.toFixed(n < 10 ? 1 : 0).replace(/\.0$/, '');
}

function PerformancePanel({ summary }: { summary: FieldAccountSummary }) {
  const hasData =
    summary.last_active_month ||
    summary.product_count > 0 ||
    Number(summary.total_9l_12mo ?? 0) > 0;
  if (!hasData) {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: GOLD,
            mb: 1.5,
          }}
        >
          Performance
        </Typography>
        <Box
          sx={{
            p: 2,
            borderRadius: 1.25,
            bgcolor: 'rgba(187, 140, 63, 0.04)',
            border: '1px dashed rgba(187, 140, 63, 0.32)',
            fontFamily: FONT_INTER,
            fontSize: 13,
            color: 'rgba(15, 10, 7, 0.6)',
          }}
        >
          No depletion shipments on record for this account yet.
        </Box>
      </Box>
    );
  }

  const lastShipment = formatMonthYear(summary.last_active_month);
  const customerSince = formatMonthYear(summary.customer_since);
  // 6-pack leads as the primary unit reps think in; the 9L
  // industry-standard equivalent trails as muted secondary.
  const cases6pk = formatCases(
    summary.total_9l_12mo == null ? null : String(toSixPacks(summary.total_9l_12mo)),
  );
  const cases9L = formatCases(summary.total_9l_12mo);
  const bucketTone = summary.activity_bucket
    ? (ACTIVITY_BUCKET_TONE[summary.activity_bucket] ?? '#7a7a78')
    : '#7a7a78';

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: GOLD,
          mb: 1.5,
        }}
      >
        Performance
      </Typography>
      <Box
        sx={{
          p: 2,
          borderRadius: 1.5,
          bgcolor: 'rgba(253, 251, 245, 0.85)',
          border: '1px solid rgba(187, 140, 63, 0.22)',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
          gap: { xs: 1.5, md: 2.25 },
        }}
      >
        <Stat label="12-mo cases (6-pk)" value={cases6pk} secondary={`${cases9L} 9L`} />
        <Stat label="Products carried" value={String(summary.product_count)} />
        <Stat label="Last shipment" value={lastShipment ?? '—'} />
        <Stat label="Customer since" value={customerSince ?? '—'} />
      </Box>
      {summary.activity_bucket && (
        <Box sx={{ mt: 1 }}>
          <Chip
            label={summary.activity_bucket}
            size="small"
            sx={{
              height: 22,
              fontSize: 11,
              fontFamily: FONT_INTER,
              fontWeight: 700,
              letterSpacing: '0.06em',
              bgcolor: `${bucketTone}1a`,
              color: bucketTone,
              border: `1px solid ${bucketTone}55`,
            }}
          />
        </Box>
      )}
    </Box>
  );
}

function Stat({ label, value, secondary }: { label: string; value: string; secondary?: string }) {
  return (
    <Box>
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: 'rgba(15, 10, 7, 0.5)',
          mb: 0.25,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: { xs: 16, md: 18 },
          fontWeight: 700,
          color: INK,
          letterSpacing: '-0.01em',
        }}
      >
        {value}
      </Typography>
      {secondary && (
        <Typography
          sx={{
            fontFamily: FONT_INTER,
            fontSize: 11,
            fontWeight: 600,
            color: 'rgba(15, 10, 7, 0.45)',
            letterSpacing: '-0.01em',
            mt: 0.15,
          }}
        >
          {secondary}
        </Typography>
      )}
    </Box>
  );
}
